import AppLayout from '@/app-layout.vue';
import PageWrapper from '@/page-wrapper.vue';

export type ContextDropdown = 'site' | 'program' | 'group' | 'teacher';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    hideFooter?: boolean;
    contextToolbar?: ContextDropdown[];
    readonly?: boolean;
    groupStatusDefault?: 'confirmed' | 'unconfirmed' | 'projected' | 'all';
    defaultReportContext?: 'enseignement' | 'planification' | 'gestion';
    /** When true, the toolbar's site dropdown is locked to `route.query.siteId` (read-only override). */
    overrideSiteFromQuery?: boolean;
    /** When true, the toolbar's program dropdown is locked to `route.query.programId` (read-only override). */
    overrideProgramFromQuery?: boolean;
    /** When true, the toolbar's group dropdown is locked to `route.query.groupId` (read-only override). */
    overrideGroupFromQuery?: boolean;
  }
}

export const getCustomRoutes = () => [
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            path: '',
            redirect: '/enseignement/tableau-de-bord',
          },
          {
            name: 'docs',
            path: '/docs/:pathMatch(.*)',
            component: () => import('@/views/docs.vue'),
            meta: { title: 'Documentation', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'preferences',
            path: '/preferences',
            component: () => import('@/views/user-preferences/index.vue'),
            meta: { title: 'Préférences', hideFooter: true, contextToolbar: [] }
          },
        ]
      },
    ]
  },
  {
    path: '/enseignement',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            name: 'enseignement-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/teaching/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-presences',
            path: 'presences',
            component: () => import('@/views/teaching/presences.vue'),
            meta: { title: 'Présences', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher'] }
          },
          {
            name: 'enseignement-examens',
            path: 'examens',
            component: () => import('@/views/teaching/exams.vue'),
            meta: { title: 'Examens', hideFooter: true, contextToolbar: ['site', 'program', 'group'], overrideSiteFromQuery: true, overrideProgramFromQuery: true, overrideGroupFromQuery: true }
          },
          {
            name: 'enseignement-assiduite',
            path: 'assiduite',
            component: () => import('@/views/teaching/attendance.vue'),
            meta: { title: 'Assiduité', hideFooter: true, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'enseignement-assignations',
            path: 'assignations',
            component: () => import('@/views/teaching/assignments.vue'),
            meta: { title: 'Assignations', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-vue-consolidee',
            path: 'vue-consolidee',
            component: () => import('@/views/teaching/consolidated-view.vue'),
            meta: { title: 'Vue consolidée', hideFooter: true, contextToolbar: ['site', 'program', 'teacher'] }
          },
          {
            name: 'enseignement-cumul-heures',
            path: 'cumul-heures',
            component: () => import('@/views/teaching/cumulative-hours.vue'),
            meta: { title: 'Cumul des heures', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'enseignement-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher'], defaultReportContext: 'enseignement' }
          },
        ]
      },
    ]
  },
  {
    path: '/planification',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            name: 'planification-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/planning/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: true, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'planification-simulation',
            path: 'simulation',
            component: () => import('@/views/planning/simulation.vue'),
            meta: { title: 'Génération d\'horaire', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'planification-etudiants',
            path: 'etudiants',
            component: () => import('@/views/planning/students.vue'),
            meta: { title: 'Gestion des étudiants', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-frequentations',
            path: 'frequentations',
            component: () => import('@/views/planning/enrollments.vue'),
            meta: { title: 'Gestion des fréquentations', hideFooter: true, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'planification-frequentation-new',
            path: 'frequentations/new',
            component: () => import('@/views/planning/frequentation-form.vue'),
            meta: { title: 'Nouvelle fréquentation', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-frequentation',
            path: 'frequentations/:id',
            component: () => import('@/views/planning/frequentation-form.vue'),
            meta: { title: 'Modifier une fréquentation', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-sequencage',
            path: 'sequencage/:id?',
            component: () => import('@/views/planning/group-sequencing.vue'),
            meta: { title: 'Séquençage', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-vue-ensemble',
            path: 'vue-ensemble',
            component: () => import('@/views/planning/groups.vue'),
            meta: { title: "Groupes", hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'planification-affectation',
            path: 'affectation/:id?',
            component: () => import('@/views/planning/group-members.vue'),
            meta: { title: 'Étudiants du groupe', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-ajustements-horaire',
            path: 'ajustements-horaire/:id?',
            component: () => import('@/views/planning/schedule-adjustments.vue'),
            meta: { title: "Ajustements d'horaire", contextToolbar: ['site', 'program'] }
          },
          {
            name: 'planification-individualise',
            path: 'individualise',
            component: () => import('@/views/planning/individual-plannings.vue'),
            meta: { title: 'Planification individualisée', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'planification-individualise-detail',
            path: 'individualise/:frequentationId',
            component: () => import('@/views/planning/individual-planning.vue'),
            meta: { title: 'Planification individualisée', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-individualise-ajustements-horaire',
            path: 'individualise/:frequentationId/ajustements-horaire',
            component: () => import('@/views/planning/individual-schedule-adjustments.vue'),
            meta: { title: "Ajustements d'horaire", hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-stages',
            path: 'stages',
            component: () => import('@/views/planning/work-placements.vue'),
            meta: { title: 'Stages', hideFooter: true, contextToolbar: ['site', 'program', 'group'] }
          },
          {
            name: 'planification-stage',
            path: 'stages/:id',
            component: () => import('@/views/planning/work-placement.vue'),
            meta: { title: 'Stage', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-entreprises',
            path: 'entreprises',
            component: () => import('@/views/planning/enterprises.vue'),
            meta: { title: 'Entreprises', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'planification-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher'], defaultReportContext: 'planification' }
          },
        ]
      },
    ]
  },
  {
    path: '/gestion',
    component: AppLayout,
    children: [
      {
        path: '',
        component: PageWrapper,
        children: [
          {
            name: 'gestion-tableau-de-bord',
            path: 'tableau-de-bord',
            component: () => import('@/views/management/dashboard.vue'),
            meta: { title: 'Tableau de bord', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-enseignants',
            path: 'enseignants',
            component: () => import('@/views/management/teachers.vue'),
            meta: { title: 'Enseignants', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-centres',
            path: 'centres',
            component: () => import('@/views/management/sites.vue'),
            meta: { title: 'Centres', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-cohortes',
            path: 'cohortes',
            component: () => import('@/views/management/cohorts.vue'),
            meta: { title: 'Cohortes', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-cohorte',
            path: 'cohortes/:id',
            component: () => import('@/views/management/cohort.vue'),
            meta: { title: 'Cohorte', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-locaux-ressources',
            path: 'locaux-ressources',
            component: () => import('@/views/management/locals-resources.vue'),
            meta: { title: 'Locaux et ressources', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-programmes',
            path: 'programmes',
            component: () => import('@/views/management/programs.vue'),
            meta: { title: 'Programmes', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-programme',
            path: 'programmes/:programId',
            component: () => import('@/views/management/program.vue'),
            meta: { title: 'Programme', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-module',
            path: 'programmes/:programId/modules/:moduleId',
            component: () => import('@/views/management/module.vue'),
            meta: { title: 'Compétence', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-gabarits',
            path: 'gabarits',
            component: () => import('@/views/management/templates.vue'),
            meta: { title: 'Gabarits', hideFooter: true, contextToolbar: ['site', 'program'] }
          },
          {
            name: 'gestion-references',
            path: 'references',
            component: () => import('@/views/management/references.vue'),
            meta: { title: 'Références', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-demonstration',
            path: 'demonstration',
            component: () => import('@/views/management/demo.vue'),
            meta: { title: 'Démonstration', hideFooter: true, contextToolbar: [] }
          },
          {
            name: 'gestion-rapports',
            path: 'rapports',
            component: () => import('@/views/reports.vue'),
            meta: { title: 'Rapports', hideFooter: true, contextToolbar: ['site', 'program', 'group', 'teacher'], defaultReportContext: 'gestion' }
          },
        ]
      },
    ]
  },
];